import React from 'react';
import { Card, Typography, Flex, Tag } from 'antd';
import { Video, VideoStatus } from '@monorepo/types';
import { useNavigate } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/de';
import 'dayjs/locale/pt-br';
import { useTranslation } from 'react-i18next';
import { upperFirst } from 'lodash';
import { getStatusColor } from '../../series/series-videos/series-videos';
import SocialIcon from '../../../components/social-provider-icon/social-provider-icon';
import { SocialProvider } from '@monorepo/types';
import { getVideoDisplayDate } from '../utils/date-utils';
import { getStatusColors } from '../utils/color-utils';

import './weekly-timeline-view.scss';

interface WeeklyTimelineViewProps {
  selectedDate: Dayjs;
  videos: Video[];
}

const WeeklyTimelineView: React.FC<WeeklyTimelineViewProps> = ({
  selectedDate,
  videos,
}) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const dayjsLocale = i18n.language === 'pt-BR' ? 'pt-br' : i18n.language;

  // Generate week days
  const weekStart = selectedDate.startOf('week');
  const weekEnd = selectedDate.endOf('week');
  const weekDays: Dayjs[] = [];
  for (let i = 0; i < 7; i++) {
    weekDays.push(weekStart.add(i, 'day'));
  }

  // Generate hours (6 AM to 11 PM)
  const hours: number[] = [];
  for (let h = 6; h <= 23; h++) {
    hours.push(h);
  }

  // Get videos for a specific day and hour
  const getVideosForDayAndHour = (day: Dayjs, hour: number): Video[] => {
    return videos.filter((video) => {
      const videoDate = dayjs(getVideoDisplayDate(video));
      return videoDate.isSame(day, 'day') && videoDate.hour() === hour;
    });
  };

  const formatHour = (hour: number): string => {
    if (hour === 0) return '12 AM';
    if (hour === 12) return '12 PM';
    if (hour < 12) return `${hour} AM`;
    return `${hour - 12} PM`;
  };

  return (
    <div className="weekly-timeline-view">
      <Typography.Title level={4} style={{ marginBottom: '16px' }}>
        {t('weeklyTimeline.weekOf', {
          start: weekStart.locale(dayjsLocale).format('MMMM D'),
          end: weekEnd.locale(dayjsLocale).format('MMMM D, YYYY'),
        })}
      </Typography.Title>

      <div className="timeline-container">
        {/* Header with days */}
        <div className="timeline-header">
          <div className="timeline-hour-label">{t('weeklyTimeline.time')}</div>
          {weekDays.map((day) => {
            const isToday = day.isSame(dayjs(), 'day');
            return (
              <div
                key={day.format('YYYY-MM-DD')}
                className={`timeline-day-header ${isToday ? 'today' : ''}`}
              >
                <Typography.Text strong>
                  {day.locale(dayjsLocale).format('ddd')}
                </Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                  {day.locale(dayjsLocale).format('MMM D')}
                </Typography.Text>
              </div>
            );
          })}
        </div>

        {/* Timeline grid */}
        <div className="timeline-grid">
          {hours.map((hour) => (
            <div key={hour} className="timeline-row">
              <div className="timeline-hour-label">
                <Typography.Text type="secondary" style={{ fontSize: '11px' }}>
                  {formatHour(hour)}
                </Typography.Text>
              </div>
              {weekDays.map((day) => {
                const dayVideos = getVideosForDayAndHour(day, hour);
                const isToday = day.isSame(dayjs(), 'day');

                return (
                  <div
                    key={`${day.format('YYYY-MM-DD')}-${hour}`}
                    className={`timeline-cell ${isToday ? 'today' : ''}`}
                  >
                    {dayVideos.map((video) => {
                      const { bgColor, borderColor } = getStatusColors(
                        video.status
                      );

                      return (
                        <div
                          key={video._id}
                          className="timeline-video-card"
                          onClick={() => navigate(`/videos/${video._id}`)}
                          style={{
                            background: bgColor,
                            borderLeft: `3px solid ${borderColor}`,
                          }}
                        >
                          <Flex
                            justify="space-between"
                            align="center"
                            style={{ marginBottom: '2px' }}
                          >
                            <Typography.Text
                              style={{
                                fontSize: '10px',
                                color: '#595959',
                                fontWeight: 500,
                              }}
                            >
                              {dayjs(getVideoDisplayDate(video)).format(
                                'h:mm A'
                              )}
                            </Typography.Text>
                            {video.publishedAccounts &&
                              video.publishedAccounts.length > 0 && (
                                <SocialIcon
                                  provider={
                                    video.publishedAccounts[0]
                                      .socialPlatform as SocialProvider
                                  }
                                  style={{ fontSize: '12px' }}
                                />
                              )}
                          </Flex>
                          <Typography.Text
                            style={{
                              fontSize: '11px',
                              fontWeight: 500,
                              lineHeight: 1.3,
                            }}
                          >
                            {video.title}
                          </Typography.Text>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WeeklyTimelineView;
